import React, { useEffect, useRef, useState } from 'react'

import AnimationLink from '../Link'

const ACTIVE_DOTS = ['#FFA600', '#FF6600', '#CC0000']

/**
 * Reusable service detail section to discover other solution areas.
 *
 * @param {string} titleBefore
 * @param {string} titleHighlight
 * @param {string} activeKey
 * @param {{ key: string, label: string, to?: string, iconType: 'dots' | 'asset', icon?: string }[]} cards
 * @param {string} [idBase] — optional stable id prefix for ARIA hooks
 */
const ServiceOtherSolutions = ({
  titleBefore,
  titleHighlight,
  activeKey,
  cards,
  idBase = 'service-other-solutions',
}) => {
  const rootRef = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || !rootRef.current) return undefined
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true)
      return undefined
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          obs.disconnect()
        }
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    )

    obs.observe(rootRef.current)
    return () => obs.disconnect()
  }, [])

  if (!cards || !cards.length) return null

  return (
    <section
      ref={rootRef}
      className={
        'service-other-solutions' +
        (visible ? ' service-other-solutions--visible' : '')
      }
      aria-labelledby={`${idBase}-heading`}
    >
      <div className="content container">
        <h2
          id={`${idBase}-heading`}
          className="service-other-solutions__heading"
        >
          {titleBefore}
          <span className="service-other-solutions__heading-accent">
            {titleHighlight}
          </span>
        </h2>

        <div className="service-other-solutions__grid" role="list">
          {cards.map(card => {
            const isActive = card.key === activeKey
            const isClickable = !isActive && !!card.to
            const cardClassName =
              'service-other-solutions__card' +
              (isActive ? ' is-active' : '') +
              (isClickable ? ' is-link' : '')

            const body = (
              <>
                <h3 className="service-other-solutions__card-title">{card.label}</h3>
                <div className="service-other-solutions__card-icon" aria-hidden="true">
                  {card.iconType === 'dots' ? (
                    <span className="service-other-solutions__dots">
                      {ACTIVE_DOTS.map((c, i) => (
                        <span key={i} className="service-other-solutions__dot" style={{ backgroundColor: c }} />
                      ))}
                    </span>
                  ) : (
                    <img
                      src={card.icon}
                      alt=""
                      width={44}
                      height={44}
                      decoding="async"
                    />
                  )}
                </div>
              </>
            )

            if (isClickable) {
              return (
                <AnimationLink
                  key={card.key}
                  role="listitem"
                  to={card.to}
                  paintDrip={false}
                  className={cardClassName}
                >
                  {body}
                </AnimationLink>
              )
            }

            return (
              <div key={card.key} role="listitem" className={cardClassName} aria-current={isActive ? 'page' : undefined}>
                {body}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default ServiceOtherSolutions
